import type { ReactNode, FunctionComponent, MouseEventHandler } from 'react';
import classnames from 'classnames';
import ButtonBase from './Base';
import Link, { RouterLink } from '../Link';
import { BUTTON_SIZE, BUTTON_TYPE } from './button-constants';
import type { ButtonSize, ButtonType } from './button-constants';
import { Icon, type IconClassName } from '../Icon';

//  Helper for determining if linking to internal app route or external route.
function isExternalLink(to: string): boolean {
  return /^https?/.test(to);
}

export type NavigationButtonProps = {
  /**
   * Where to link to.
   */
  to: string;
  /**
   * Button contents
   */
  children: ReactNode;
  /**
   * If a given icon should be rendered to the left of props.children.
   */
  icon?: IconClassName;
  /**
   * If an icon is provided, should it spin
   */
  iconHasSpin?: boolean;
  /**
   * If the link should be disabled or not
   */
  isDisabled?: boolean;
  /**
   * If the button should be 100% width;
   */
  isFullWidth?: boolean;
  /**
   * If the link should open in a new tab (internal router links only)
   */
  openInNewTab?: boolean;
  /**
   * ButtonType styling for the "button" (link). @default BUTTON_TYPE.PRIMARY
   */
  type?: ButtonType;
  /**
   * ButtonSize styling for the "button" (link). @default BUTTON_SIZE.SMALL
   */
  size?: ButtonSize;
  /**
   * Stop the click event from propagating upwards
   */
  stopPropagation?: boolean;
  /**
   * Adds a `data-test-id` attribute, useful for selenium testing
   */
  testId?: string;
};

/**
 * For buttons which are used for navigation, either to an internal app route or to an external page.
 */
const NavigationButton: FunctionComponent<NavigationButtonProps> = ({
  to,
  children,
  icon,
  iconHasSpin,
  size = BUTTON_SIZE.SMALL,
  type = BUTTON_TYPE.PRIMARY,
  isDisabled,
  isFullWidth,
  openInNewTab,
  stopPropagation = false,
  testId,
}) => {
  //  Determine the correct link component to use here.
  const LinkComponent = isExternalLink(to)
    ? Link
    : RouterLink;

  const onClick: MouseEventHandler<HTMLAnchorElement> | undefined = stopPropagation
    ? (e) => e?.stopPropagation()
    : undefined;

  return (
    <ButtonBase
      type={type}
      size={size}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
    >
      {getButtonProps => {
        const { className, disabled: isButtonDisabled } = getButtonProps();
        return (
          <LinkComponent
            to={isDisabled ? '#' : to}
            className={className}
            disabled={isButtonDisabled}
            openInNewTab={openInNewTab}
            onClick={onClick}
            testId={testId}
          >
            <span className="fossa-button__contents">
              {icon && (
                <span className={classnames('fossa-button__icon', {
                  'fossa-button__icon--solo': !children, // Remove margin if only child
                })}>
                  <Icon icon={icon} hasSpin={iconHasSpin} />
                </span>
              )}
              <span className="fossa-button__text">
                {children}
              </span>
            </span>
          </LinkComponent>
        );
      }}
    </ButtonBase>
  );
};

export default NavigationButton;
